/**
 * TradeWeekPicker.jsx — shared trade-week selector (issues #19, #20, #22).
 *
 * Renders three compact arrows + a clickable center indicator that opens
 * a popover with:
 *   - a "weeks ago" number input (#20) for arbitrary jumps
 *   - an "All time" button (when allowAllTime is set; rendered as ⌛)
 *   - a custom date+time range picker (#22) that emits {startTs, endTs}
 *     in unix seconds when applied
 *
 * Props:
 *   value:        current state — either `{ weekOffset }` or `{ startTs, endTs }`
 *   onChange:     fn(next) where next is one of those shapes (or `{ weekOffset: -1 }`)
 *   maxWeeksAgo:  numeric upper bound for arrows (default 51)
 *   allowAllTime: render the ⌛ All-time arrow (default true)
 *   allowCustom:  render the custom date+time range section (default true)
 *   weekLabel:    optional override for the centre label (e.g. server-supplied "This week")
 */

import { Show, createSignal, onCleanup, onMount, createMemo, createEffect } from 'solid-js';
import { Portal } from 'solid-js/web';
import './TradeWeekPicker.css';
import { snapToReset, formatRange, RESET_WEEK_MS } from '../lib/traderReset.js';

function fmtForInput(tsSec) {
  if (!tsSec) return '';
  const d = new Date(tsSec * 1000);
  // datetime-local expects YYYY-MM-DDTHH:MM in *local* time.
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function parseInputTs(s) {
  if (!s) return null;
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return null;
  return Math.floor(t / 1000);
}

export default function TradeWeekPicker(props) {
  const maxWeeksAgo  = () => Number.isFinite(props.maxWeeksAgo) ? props.maxWeeksAgo : 51;
  const allowAllTime = () => props.allowAllTime !== false;
  const allowCustom  = () => props.allowCustom  !== false;
  const nativeTitles = () => props.nativeTitles !== false;

  const isCustom = () => props.value?.startTs != null && props.value?.endTs != null;
  const isAllTime = () => !isCustom() && props.value?.weekOffset === -1;
  const offset = () => isCustom() || isAllTime() ? null : (Number(props.value?.weekOffset) || 0);

  const centerLabel = createMemo(() => {
    if (isCustom()) return 'custom';
    if (isAllTime()) return '⌛';
    if (offset() === 0) return 'now';
    if (offset() === 1) return '−1w';
    return `−${offset()}w`;
  });
  const centerTitle = createMemo(() => {
    if (isCustom()) return 'Custom range — click to edit';
    if (isAllTime()) return 'All time — click to pick a specific week';
    if (offset() === 0) return 'This trade week — click to pick a specific week';
    return `${offset()} week${offset() === 1 ? '' : 's'} ago — click to pick a different week`;
  });
  const titleOrUndefined = (text) => nativeTitles() ? text : undefined;

  const [open, setOpen] = createSignal(false);
  const [pos,  setPos]  = createSignal({ x: 0, y: 0 });
  const [draftWeeks, setDraftWeeks] = createSignal(offset() ?? 0);
  const [draftStart, setDraftStart] = createSignal(fmtForInput(props.value?.startTs));
  const [draftEnd,   setDraftEnd]   = createSignal(fmtForInput(props.value?.endTs));
  let anchorEl = null;
  let popEl = null;

  function openPop() {
    setDraftWeeks(offset() ?? 0);
    setDraftStart(fmtForInput(props.value?.startTs));
    setDraftEnd(fmtForInput(props.value?.endTs));
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const POP_W = 280;
      let x = rect.left + rect.width / 2 - POP_W / 2;
      x = Math.max(8, Math.min(window.innerWidth - POP_W - 8, x));
      const y = rect.bottom + 6;
      setPos({ x, y });
    }
    setOpen(true);
  }
  function closePop() { setOpen(false); }

  function applyWeeks() {
    const v = Math.max(0, Math.min(maxWeeksAgo(), Math.floor(Number(draftWeeks()) || 0)));
    props.onChange?.({ weekOffset: v });
    closePop();
  }
  function applyAllTime() {
    props.onChange?.({ weekOffset: -1 });
    closePop();
  }
  function applyRange() {
    const sRaw = parseInputTs(draftStart());
    const eRaw = parseInputTs(draftEnd());
    if (sRaw == null || eRaw == null || eRaw <= sRaw) return;
    // #283 — snap to ESO trader reset boundaries (Tuesday 19:00 UTC
    // / 20:00 UTC outside DST). Start floors to the most recent
    // reset, end ceils to the next reset, so any user-entered range
    // becomes a contiguous block of whole trade weeks.
    let sMs = snapToReset(sRaw * 1000, 'floor');
    let eMs = snapToReset(eRaw * 1000, 'ceil');
    if (eMs <= sMs) eMs = sMs + RESET_WEEK_MS;
    props.onChange?.({ startTs: Math.floor(sMs / 1000), endTs: Math.floor(eMs / 1000) });
    closePop();
  }

  // #283 — preview of the snapped range so users see exactly which
  // trade weeks they'll get before clicking Apply.
  const snappedRangeLabel = createMemo(() => {
    const sRaw = parseInputTs(draftStart());
    const eRaw = parseInputTs(draftEnd());
    if (sRaw == null || eRaw == null || eRaw <= sRaw) return '';
    const sMs = snapToReset(sRaw * 1000, 'floor');
    let eMs = snapToReset(eRaw * 1000, 'ceil');
    if (eMs <= sMs) eMs = sMs + RESET_WEEK_MS;
    const weeks = Math.round((eMs - sMs) / RESET_WEEK_MS);
    return `${formatRange(sMs, eMs)} · ${weeks} trade week${weeks === 1 ? '' : 's'}`;
  });

  function step(delta) {
    if (isCustom() || isAllTime()) {
      props.onChange?.({ weekOffset: 0 });
      return;
    }
    const next = Math.max(0, Math.min(maxWeeksAgo(), (offset() ?? 0) + delta));
    props.onChange?.({ weekOffset: next });
  }

  function onDocClick(ev) {
    if (!open()) return;
    if (popEl && popEl.contains(ev.target)) return;
    if (anchorEl && anchorEl.contains(ev.target)) return;
    closePop();
  }
  function onKey(ev) {
    if (ev.key === 'Escape' && open()) { closePop(); }
    else if (ev.key === 'Enter' && open()) {
      // Let inputs decide — just close on plain Enter from outside.
    }
  }
  onMount(() => {
    document.addEventListener('mousedown', onDocClick, true);
    window.addEventListener('keydown', onKey);
  });
  onCleanup(() => {
    document.removeEventListener('mousedown', onDocClick, true);
    window.removeEventListener('keydown', onKey);
  });

  // #283 — inline numeric input mode lets callers (e.g. kiosk widgets)
  // expose a directly-typeable weeks-ago entry without forcing the
  // user to open the popover first. The arrow steppers still work the
  // same way as before.
  const inlineNumeric = () => props.inlineNumeric === true;
  const [inlineDraft, setInlineDraft] = createSignal(String(offset() ?? 0));
  // Keep the inline draft in sync with the canonical offset when it
  // changes externally (arrows, popover apply, etc.).
  createEffect(() => {
    if (isCustom() || isAllTime()) return;
    setInlineDraft(String(offset() ?? 0));
  });
  function applyInlineNumeric() {
    const v = Math.max(0, Math.min(maxWeeksAgo(), Math.floor(Number(inlineDraft()) || 0)));
    setInlineDraft(String(v));
    props.onChange?.({ weekOffset: v });
  }

  return (
    <span class="v2-twp" ref={(el) => (anchorEl = el)}>
      <button class="v2-widget-hdr__btn v2-twp__arrow" title={titleOrUndefined('Older trade week')}
        disabled={!isCustom() && !isAllTime() && offset() >= maxWeeksAgo()}
        onClick={() => step(+1)}>‹</button>
      <Show when={inlineNumeric() && !isCustom() && !isAllTime()} fallback={
        <button class="v2-widget-hdr__btn v2-twp__center"
          classList={{ 'is-now': !isCustom() && !isAllTime() && offset() === 0,
                       'is-custom': isCustom(),
                       'is-alltime': isAllTime() }}
          title={titleOrUndefined(centerTitle())}
          onClick={openPop}>{centerLabel()}</button>
      }>
        <input class="v2-input v2-twp__inline-num" type="number"
          min="0" max={maxWeeksAgo()} step="1"
          title={titleOrUndefined('Weeks ago — type a number or click for date range')}
          value={inlineDraft()}
          onInput={(e) => setInlineDraft(e.currentTarget.value)}
          onChange={applyInlineNumeric}
          onKeyDown={(e) => { if (e.key === 'Enter') { applyInlineNumeric(); e.currentTarget.blur(); } }}
          onClick={(e) => e.stopPropagation()}
          onDblClick={openPop}
        />
        <button class="v2-widget-hdr__btn v2-twp__center v2-twp__center--mini"
          title={titleOrUndefined(`${centerTitle()} — click for date range`)}
          onClick={openPop}>w</button>
      </Show>
      <button class="v2-widget-hdr__btn v2-twp__arrow" title={titleOrUndefined('Newer trade week')}
        disabled={!isCustom() && !isAllTime() && offset() <= 0}
        onClick={() => step(-1)}>›</button>
      <Show when={allowAllTime()}>
        <button class="v2-widget-hdr__btn v2-twp__alltime"
          classList={{ 'is-active': isAllTime() }}
          title={titleOrUndefined('All time')}
          onClick={() => props.onChange?.({ weekOffset: -1 })}>⌛</button>
      </Show>
      <Show when={open()}>
        <Portal>
          <div class="v2-twp-pop" ref={(el) => (popEl = el)}
            style={{ left: pos().x + 'px', top: pos().y + 'px' }}>
            <div class="v2-twp-pop__row">
              <label class="v2-twp-pop__label">Weeks ago</label>
              <input class="v2-input v2-twp-pop__num" type="number"
                min="0" max={maxWeeksAgo()} step="1"
                value={draftWeeks()}
                onInput={(e) => setDraftWeeks(e.currentTarget.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyWeeks(); }}
              />
              <button class="v2-btn-sm" onClick={applyWeeks}>Go</button>
            </div>
            <Show when={allowAllTime()}>
              <div class="v2-twp-pop__row">
                <button class="v2-btn-sm v2-twp-pop__alltime" onClick={applyAllTime}>⌛ All time</button>
              </div>
            </Show>
            <Show when={allowCustom()}>
              <div class="v2-twp-pop__sep">or custom range (snaps to Tue 3 PM ET reset)</div>
              <div class="v2-twp-pop__row">
                <label class="v2-twp-pop__label">From</label>
                <input class="v2-input v2-twp-pop__dt" type="datetime-local"
                  value={draftStart()} onInput={(e) => setDraftStart(e.currentTarget.value)} />
              </div>
              <div class="v2-twp-pop__row">
                <label class="v2-twp-pop__label">To</label>
                <input class="v2-input v2-twp-pop__dt" type="datetime-local"
                  value={draftEnd()} onInput={(e) => setDraftEnd(e.currentTarget.value)} />
              </div>
              <Show when={snappedRangeLabel()}>
                <div class="v2-twp-pop__hint" title={titleOrUndefined('Snapped to ESO trader reset boundaries')}>
                  → {snappedRangeLabel()}
                </div>
              </Show>
              <div class="v2-twp-pop__row">
                <button class="v2-btn-sm v2-twp-pop__apply"
                  disabled={!parseInputTs(draftStart()) || !parseInputTs(draftEnd())
                            || (parseInputTs(draftEnd()) <= parseInputTs(draftStart()))}
                  onClick={applyRange}>Apply range</button>
                <button class="v2-btn-sm" onClick={closePop}>Cancel</button>
              </div>
            </Show>
          </div>
        </Portal>
      </Show>
    </span>
  );
}
